import { useState, useCallback, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { Button } from '@lecartte/shared-ui/components/Button';
import { FormInput } from '@lecartte/shared-ui/components/FormInput';
import { LecartteSelect } from '@lecartte/shared-ui/components/fields/LecartteSelect';
import { Badge } from '@lecartte/shared-ui/components/Badge';
import { CloseButton } from '@lecartte/shared-ui/components/RoundIconButton';
import { PROMOTIONAL_CODES_QUERY } from '@/graphql/PromotionalCodeQuery';
import { CREATE_PROMOTIONAL_CODE_MUTATION } from '@/graphql/CreatePromotionalCodeMutation';
import { UPDATE_PROMOTIONAL_CODE_MUTATION } from '@/graphql/UpdatePromotionalCodeMutation';
import { DELETE_PROMOTIONAL_CODE_MUTATION } from '@/graphql/DeletePromotionalCodeMutation';
import type { PromotionalCodeQuery } from '@/__generated__/PromotionalCodeQuery.graphql';
import type { CreatePromotionalCodeMutation } from '@/__generated__/CreatePromotionalCodeMutation.graphql';
import type { UpdatePromotionalCodeMutation } from '@/__generated__/UpdatePromotionalCodeMutation.graphql';
import type { DeletePromotionalCodeMutation } from '@/__generated__/DeletePromotionalCodeMutation.graphql';

const PLANS = [
  { key: 'DEGUSTER', name: 'Deguster', price: 'Gratis', status: 'Publico', statusVariant: 'success' as const },
  { key: 'GOURMET', name: 'Gourmet', price: 'R$ 79,90/mes', status: 'Publico', statusVariant: 'success' as const },
  { key: 'BANQUET', name: 'Banquete', price: 'R$ 199,90/mes', status: 'Publico', statusVariant: 'success' as const },
  { key: 'TWOHDSC', name: 'TWOHDSC', price: 'R$ 5,00/mes', status: 'Interno', statusVariant: 'info' as const },
];

function formatCents({ cents }: { cents: number }): string {
  return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function formatDate({ dateStr }: { dateStr: string }): string {
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

type PromoCode = {
  readonly id: string;
  readonly code: string;
  readonly plan: string;
  readonly planName: string;
  readonly planValueCents: number;
  readonly discountType: string | null;
  readonly discountValue: number | null;
  readonly discountDuration: string | null;
  readonly durationMonths: number | null;
  readonly appliesTo: readonly string[] | null;
  readonly isActive: boolean;
  readonly maxUses: number;
  readonly currentUses: number;
  readonly expiresAt: string | null;
  readonly createdAt: string;
};

function formatDiscount({ discountType, discountValue }: { discountType: string | null; discountValue: number | null }): string {
  if (!discountType || discountValue == null) return '-';
  if (discountType === 'PERCENTAGE') return `-${discountValue}%`;
  if (discountType === 'FIXED') return `-${formatCents({ cents: discountValue })}`;
  return '-';
}

function couponStatus({ promo }: { promo: PromoCode }): { label: string; variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral' } {
  if (promo.expiresAt && new Date(promo.expiresAt) < new Date()) {
    return { label: 'Expirado', variant: 'danger' };
  }
  if (promo.maxUses > 0 && promo.currentUses >= promo.maxUses) {
    return { label: 'Esgotado', variant: 'warning' };
  }
  if (!promo.isActive) {
    return { label: 'Inativo', variant: 'neutral' };
  }
  return { label: 'Ativo', variant: 'success' };
}

function OverviewTab() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {PLANS.map((plan) => (
        <div key={plan.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-lg text-slate-900">{plan.name}</h3>
            <Badge label={plan.status} variant={plan.statusVariant} />
          </div>
          <p className="text-slate-500 text-sm">{plan.price}</p>
          <p className="font-mono text-xs text-slate-400">{plan.key}</p>
        </div>
      ))}
    </div>
  );
}

function deriveInitialCouponType({ promo }: { promo: PromoCode | null }): string {
  if (!promo) return 'unlock';
  if (promo.discountType === 'PERCENTAGE' || promo.discountType === 'FIXED') return promo.discountType;
  return 'unlock';
}

function deriveInitialDiscountValue({ promo }: { promo: PromoCode | null }): string {
  if (!promo || !promo.discountValue) return '';
  if (promo.discountType === 'FIXED') return (promo.discountValue / 100).toFixed(2).replace('.', ',');
  return String(promo.discountValue);
}

function deriveInitialDiscountDuration({ promo }: { promo: PromoCode | null }): string {
  if (!promo || !promo.discountDuration) return 'N_MONTHS';
  if (promo.discountDuration === 'FIRST_ONLY') return 'N_MONTHS';
  return promo.discountDuration;
}

function deriveInitialDurationMonths({ promo }: { promo: PromoCode | null }): string {
  if (!promo || !promo.durationMonths) {
    if (promo?.discountDuration === 'FIRST_ONLY') return '1';
    return '1';
  }
  return String(promo.durationMonths);
}

function formatDuration({ discountType, discountDuration, durationMonths }: { discountType: string | null; discountDuration: string | null; durationMonths: number | null }): string {
  if (!discountType) return '-';
  if (!discountDuration || discountDuration === 'FIRST_ONLY') return '1a mensalidade';
  if (discountDuration === 'RECURRING') return 'Recorrente';
  if (discountDuration === 'N_MONTHS' && durationMonths) return durationMonths === 1 ? '1 mês' : `${durationMonths} meses`;
  return '-';
}

function deriveInitialAppliesTo({ promo }: { promo: PromoCode | null }): string[] {
  if (!promo || !promo.appliesTo) return [];
  return [...promo.appliesTo];
}

const DISCOUNT_PLANS = [
  { key: 'GOURMET', name: 'Gourmet' },
  { key: 'BANQUET', name: 'Banquete' },
];

function CouponFormModal({
  onClose,
  onSaved,
  editingPromo,
}: {
  onClose: () => void;
  onSaved: () => void;
  editingPromo: PromoCode | null;
}) {
  const isEditing = editingPromo !== null;
  const [code, setCode] = useState(editingPromo?.code ?? '');
  const [plan, setPlan] = useState(editingPromo?.plan ?? 'TWOHDSC');
  const [couponType, setCouponType] = useState(() => deriveInitialCouponType({ promo: editingPromo }));
  const [discountValue, setDiscountValue] = useState(() => deriveInitialDiscountValue({ promo: editingPromo }));
  const [discountDuration, setDiscountDuration] = useState(() => deriveInitialDiscountDuration({ promo: editingPromo }));
  const [durationMonths, setDurationMonths] = useState(() => deriveInitialDurationMonths({ promo: editingPromo }));
  const [appliesTo, setAppliesTo] = useState<string[]>(() => deriveInitialAppliesTo({ promo: editingPromo }));
  const [maxUses, setMaxUses] = useState(String(editingPromo?.maxUses ?? 0));
  const [expiresAt, setExpiresAt] = useState(editingPromo?.expiresAt?.split('T')[0] ?? '');
  const [isActive, setIsActive] = useState(editingPromo?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDiscount = couponType === 'PERCENTAGE' || couponType === 'FIXED';

  const [commitCreate] = useMutation<CreatePromotionalCodeMutation>(CREATE_PROMOTIONAL_CODE_MUTATION);
  const [commitUpdate] = useMutation<UpdatePromotionalCodeMutation>(UPDATE_PROMOTIONAL_CODE_MUTATION);

  const toggleAppliesTo = useCallback(({ planKey }: { planKey: string }) => {
    setAppliesTo((prev) =>
      prev.includes(planKey) ? prev.filter((p) => p !== planKey) : [...prev, planKey],
    );
  }, []);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    if (isDiscount && appliesTo.length === 0) { setError('Selecione um plano'); return; }
    if (isDiscount && !discountValue) { setError('Informe o valor do desconto'); return; }
    setSaving(true);
    setError(null);

    const discountFields = isDiscount
      ? {
          discountType: couponType as any,
          discountValue: couponType === 'FIXED' ? Math.round(Number(discountValue.replace(',', '.')) * 100) : Number(discountValue),
          discountDuration: discountDuration as any,
          durationMonths: discountDuration === 'N_MONTHS' ? Number(durationMonths) : null,
          appliesTo,
          plan: null,
        }
      : {
          discountType: null,
          discountValue: null,
          discountDuration: null,
          durationMonths: null,
          appliesTo: null,
          plan: plan as any,
        };

    if (isEditing) {
      const { plan: _plan, ...updateDiscountFields } = discountFields;
      commitUpdate({
        variables: {
          input: {
            id: editingPromo.id,
            code: code.trim(),
            maxUses: Number(maxUses),
            expiresAt: expiresAt || null,
            isActive,
            ...updateDiscountFields,
          } as any,
        },
        onCompleted: () => {
          setSaving(false);
          onSaved();
          onClose();
        },
        onError: (err) => {
          setError(err.message);
          setSaving(false);
        },
      });
    } else {
      commitCreate({
        variables: {
          input: {
            code: code.trim(),
            maxUses: Number(maxUses),
            expiresAt: expiresAt || null,
            ...discountFields,
          } as any,
        },
        onCompleted: () => {
          setSaving(false);
          onSaved();
          onClose();
        },
        onError: (err) => {
          setError(err.message);
          setSaving(false);
        },
      });
    }
  }, [code, plan, couponType, discountValue, discountDuration, durationMonths, appliesTo, maxUses, expiresAt, isActive, isDiscount, isEditing, editingPromo, commitCreate, commitUpdate, onSaved, onClose]);

  const getToggleTrackClass = ({ active }: { active: boolean }) =>
    `relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${active ? 'bg-primary' : 'bg-slate-300'}`;

  const getToggleKnobClass = ({ active }: { active: boolean }) =>
    `inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${active ? 'translate-x-6' : 'translate-x-1'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-lg text-slate-900">
            {isEditing ? 'Editar cupom' : 'Criar cupom'}
          </h2>
          <CloseButton onClick={onClose} />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">Código</label>
            <FormInput
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="EX: PROMO2026"
              required
              className="font-mono uppercase"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="coupon-type" className="text-xs font-medium text-slate-600">Tipo</label>
            <LecartteSelect
              id="coupon-type"
              value={couponType}
              onValueChange={({ value }) => setCouponType(value)}
              options={[
                { value: 'unlock', label: 'Desbloquear plano interno' },
                { value: 'PERCENTAGE', label: 'Desconto percentual' },
                { value: 'FIXED', label: 'Desconto em valor fixo' },
              ]}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="coupon-plan" className="text-xs font-medium text-slate-600">Plano</label>
            <LecartteSelect
              id="coupon-plan"
              value={isDiscount ? (appliesTo[0] ?? null) : plan}
              onValueChange={({ value }) => {
                if (isDiscount) {
                  setAppliesTo(value ? [value] : []);
                } else {
                  setPlan(value);
                }
              }}
              placeholder={isDiscount ? 'Selecione um plano' : undefined}
              options={isDiscount ? DISCOUNT_PLANS.map((dp) => ({ value: dp.key, label: dp.name })) : [{ value: 'TWOHDSC', label: 'TWOHDSC' }]}
            />
          </div>

          {isDiscount && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-600">Valor do desconto</label>
              <div className="flex items-center gap-2">
                {couponType === 'FIXED' && (
                  <span className="text-sm text-slate-500">R$</span>
                )}
                <FormInput
                  type="text"
                  inputMode={couponType === 'FIXED' ? 'numeric' : 'numeric'}
                  value={discountValue}
                  onChange={(e) => {
                    if (couponType === 'FIXED') {
                      const digits = e.target.value.replace(/\D/g, '');
                      if (!digits) { setDiscountValue(''); return; }
                      const cents = parseInt(digits, 10);
                      setDiscountValue((cents / 100).toFixed(2).replace('.', ','));
                    } else {
                      const raw = e.target.value.replace(/\D/g, '');
                      const num = Number(raw);
                      if (num <= 100) setDiscountValue(raw);
                    }
                  }}
                  placeholder={couponType === 'FIXED' ? '0,00' : '15'}
                  required
                  className="flex-1"
                />
                {couponType === 'PERCENTAGE' && (
                  <span className="text-sm text-slate-500">%</span>
                )}
              </div>
            </div>
          )}

          {isDiscount && (
            <div className="flex flex-col gap-1">
              <label htmlFor="coupon-duration" className="text-xs font-medium text-slate-600">Duração do desconto</label>
              <LecartteSelect
                id="coupon-duration"
                value={discountDuration}
                onValueChange={({ value }) => setDiscountDuration(value)}
                options={[
                  { value: 'N_MONTHS', label: 'Por N meses' },
                  { value: 'RECURRING', label: 'Recorrente' },
                ]}
              />
            </div>
          )}

          {isDiscount && discountDuration === 'N_MONTHS' && (
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-slate-600">Quantidade de meses</label>
              <FormInput
                type="number"
                value={durationMonths}
                onChange={(e) => setDurationMonths(e.target.value)}
                min={1}
                placeholder="3"
                required
              />
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">
              Limite de usos <span className="text-slate-400 font-normal">(0 = ilimitado)</span>
            </label>
            <FormInput
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              min={0}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">
              Data de expiração <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <FormInput
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>

          {isEditing && (
            <div className="flex items-center gap-3 pt-1">
              <label className="text-xs font-medium text-slate-600">Ativo</label>
              <button
                type="button"
                role="switch"
                aria-checked={isActive}
                onClick={() => setIsActive(!isActive)}
                className={getToggleTrackClass({ active: isActive })}
              >
                <span className={getToggleKnobClass({ active: isActive })} />
              </button>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !code.trim()} loading={saving} className="flex-1">
              {saving ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar'}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function DeleteConfirmModal({
  promoCode,
  onClose,
  onDeleted,
}: {
  promoCode: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 flex flex-col gap-4"
      >
        <h2 className="font-display font-bold text-lg text-slate-900">Excluir cupom</h2>
        <p className="text-sm text-slate-500">
          Tem certeza que deseja excluir o cupom <strong className="text-slate-700">{promoCode}</strong>? Esta acao nao pode ser desfeita.
        </p>
        <div className="flex gap-2 pt-1">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancelar
          </Button>
          <Button type="button" variant="danger" onClick={onDeleted} className="flex-1">
            Excluir
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function CouponsTable({ fetchKey }: { fetchKey: number }) {
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deletingPromo, setDeletingPromo] = useState<PromoCode | null>(null);
  const [localFetchKey, setLocalFetchKey] = useState(0);

  const data = useLazyLoadQuery<PromotionalCodeQuery>(
    PROMOTIONAL_CODES_QUERY,
    {},
    { fetchKey: fetchKey + localFetchKey, fetchPolicy: 'network-only' },
  );

  const [commitDelete] = useMutation<DeletePromotionalCodeMutation>(DELETE_PROMOTIONAL_CODE_MUTATION);

  const handleDelete = useCallback(({ id }: { id: string }) => {
    commitDelete({
      variables: { id },
      onCompleted: () => {
        setDeletingPromo(null);
        setLocalFetchKey((k) => k + 1);
      },
      onError: () => {
        setDeletingPromo(null);
      },
    });
  }, [commitDelete]);

  const handleSaved = useCallback(() => {
    setLocalFetchKey((k) => k + 1);
  }, []);

  const promos = data.adminPromotionalCodes as readonly PromoCode[];

  return (
    <>
      {(showCreate || editingPromo) && (
        <CouponFormModal
          editingPromo={editingPromo}
          onClose={() => { setShowCreate(false); setEditingPromo(null); }}
          onSaved={handleSaved}
        />
      )}

      {deletingPromo && (
        <DeleteConfirmModal
          promoCode={deletingPromo.code}
          onClose={() => setDeletingPromo(null)}
          onDeleted={() => handleDelete({ id: deletingPromo.id })}
        />
      )}

      <div className="flex items-center justify-end mb-4">
        <Button onClick={() => setShowCreate(true)}>
          <Plus size={15} />
          Criar cupom
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Código</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Plano</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Desconto</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Duração</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Usos</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Limite</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Expira em</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {promos.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-slate-400 text-sm">
                  Nenhum cupom encontrado.
                </td>
              </tr>
            ) : (
              promos.map((promo) => {
                const status = couponStatus({ promo });
                return (
                  <tr key={promo.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 font-mono font-semibold text-slate-800">{promo.code}</td>
                    <td className="px-4 py-3 text-slate-600">
                      {promo.planName}
                      <span className="text-slate-400 text-xs ml-1">({formatCents({ cents: promo.planValueCents })})</span>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">
                      {formatDiscount({ discountType: promo.discountType, discountValue: promo.discountValue })}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">
                      {formatDuration({ discountType: promo.discountType, discountDuration: promo.discountDuration, durationMonths: promo.durationMonths })}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-600">{promo.currentUses}</td>
                    <td className="px-4 py-3 text-center text-slate-600">{promo.maxUses === 0 ? 'Ilimitado' : promo.maxUses}</td>
                    <td className="px-4 py-3 text-slate-500">
                      {promo.expiresAt ? formatDate({ dateStr: promo.expiresAt }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge label={status.label} variant={status.variant} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingPromo(promo as PromoCode)}
                          title="Editar"
                          className="px-1.5"
                        >
                          <Pencil size={13} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingPromo(promo as PromoCode)}
                          title="Excluir"
                          className="px-1.5 text-slate-600 hover:text-red-600"
                        >
                          <Trash2 size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CouponsTab() {
  const [fetchKey] = useState(0);

  return (
    <Suspense
      fallback={
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center text-slate-400 text-sm">
          Carregando...
        </div>
      }
    >
      <CouponsTable fetchKey={fetchKey} />
    </Suspense>
  );
}

export function PlansPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'coupons'>('overview');

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Planos</h1>
        <p className="text-slate-500 text-sm mt-1">
          Gerenciamento de planos e cupons promocionais.
        </p>
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setActiveTab('overview')}
          className={activeTab === 'overview' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}
        >
          Visão Geral
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setActiveTab('coupons')}
          className={activeTab === 'coupons' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}
        >
          Cupons
        </Button>
      </div>

      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'coupons' && <CouponsTab />}
    </div>
  );
}
